import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useDispatch } from "react-redux";
import { useQuerySchemaQuery } from "../../service/apiSlices/sematicModel";
import { setShowConnectionExpiredModal } from "../../service/apiSlices/staticApi";
import useDebounce from "../../hooks/useDebounce";
import { TOKENS } from "../../utilities/Constants/Constants";
import { showErrorToast, showInfoToast } from "../../utilities/toastUtils";
import meiLogoColored from '../../assets/images/meilogo.png'


const QueryTablesDataContext = createContext({});
export const useQueryTablesDataContext: any = () =>
  useContext(QueryTablesDataContext);

const QueryTablesDataProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const connection_Id = sessionStorage.getItem(TOKENS.CONNECTION_ID);
  const dispatch = useDispatch();

  const [searchText, setSearchText] = useState<string>("");
  const [tableList, setTableList] = useState<any>(null);

  const debouncedSearchText = useDebounce(searchText);

  const {
    refetch,
    isLoading,
    isFetching
  } = useQuerySchemaQuery(
    { connection_key: connection_Id, search: debouncedSearchText },
    {
      skip: !connection_Id,
      refetchOnMountOrArgChange: true
    }
  );

  // Initial fetch when component mounts or dependencies change
  useEffect(() => {
    if (connection_Id) {
      fetchTableList();
    }
  }, [connection_Id, debouncedSearchText]);

  // Function to fetch table list data
  const fetchTableList = useCallback(() => {
    if (connection_Id) {
      refetch().then((response) => {
        if (response?.data?.status_code === 400) {
          dispatch(setShowConnectionExpiredModal())
          return;
        }
        if (Array.isArray(response?.data?.data) && response?.data?.data?.length < 1) {
          showInfoToast('No results found')
          return;
        };
        if (response.data) {
          setTableList(response.data);
        }
      }).catch(error => {
        showErrorToast('Error fetching the table list')
        console.error('Error fetching the table list', error)
      });
    }
  }, [connection_Id, refetch]);

  const overallQueryItemMapByTableName = useMemo(() => {
    if (!tableList?.data) return {};
    return tableList.data.reduce(
      (acc: any, item: { table_name: string; columns: string[] }) => {
        acc[item.table_name] = item;
        return acc;
      },
      {}
    );
  }, [tableList]);

  const handleOnSearchTextChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setSearchText(e.target.value);
    },
    []
  );

  const values: any = useMemo(
    () => ({
      tableList: tableList?.data,
      overallQueryItemMapByTableName,
      isTableListLoading: isFetching,
      handleOnSearchTextChange,
    }),
    [handleOnSearchTextChange, isFetching, overallQueryItemMapByTableName, tableList?.data]
  );

  return (
    <QueryTablesDataContext.Provider value={values}>
      {(isLoading || isFetching) && (
        <div className="mei-logo-spinner-wrapper">
          <div className="loader-overlay"></div>
          <div className="mei-logo-spinner-container position-absolutes">
            <img src={meiLogoColored} alt='' className="mei-logo-spinner" />
          </div>
        </div>
      )}
      {children}
    </QueryTablesDataContext.Provider>
  );
};

export default QueryTablesDataProvider;
